import { useContext, useState, useMemo, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { BoxFull, Button, Calendar } from "iuvity-ui-components";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../context/index";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { InputText } from "iuvity-ui-components/dist/inputtext";
import { TableHeader } from "../datatable/TableHeader";
import { Actions } from "../datatable/Actions";
import { Column } from "primereact/column";
import { FormSkeleton } from "../skeleton/FormSkeleton";
import { FilterMatchMode } from "primereact/api";
import { CustomDialog, DialogProps } from "../dialog/CustomDialog";
import { formatStartDateAndEndDate, formatStringToDate2 } from "../../helpers/formatDate";
import { EmptyTemplate } from "../datatable/EmptyTemplate";
import noResultsImage from "../../assets/Illustration_NoResults.svg";
import reactiveIcon from "../../assets/icon-restore.svg";
import { Tag } from "iuvity-ui-components/dist/tag";
import { ActionType } from "../../hooks/usePermissions";
import { REVALIDATE_FORM_PROPS } from "../../constants";
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from "primereact/multiselect";
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";

interface PermissionAdminProps {
  data: any;
  permissionsAction: (action: ActionType, data: any) => Promise<string>;
  reloadFn: () => void;
  isDeleteComponent?: boolean;
}

const formBlankValues = {
  functionalityId: "",
  privilegeId: "",
  protocolId: "",
  methodId: "",
  port: "",
  pagePath: "",
  servicePath: "",
  expeditionDate: "",
  expirationDate: "",
  statusId: "",
  createdAt: "",
};

export const PermissionAdminComponent: React.FC<PermissionAdminProps> = ({
  isDeleteComponent = false,
  data,
  permissionsAction,
  reloadFn,
}) => {
  const navigate = useNavigate();
  const {
    t,
    appState: { language, pageFilter, permissiondInfoByRole },
    setAppState,
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: formBlankValues,
  });

  const { control, setValue, getValues } = form;

  const columns = useMemo(() => {
    if (language) {
      let columList = [
        {
          field: "functionalityId",
          header: t("Permissions.functionalityIdLabel"),
        },
        {
          field: "privilegeId",
          header: t("Permissions.privilegeIdLabel"),
        },
        {
          field: "statusId",
          header: t("Permissions.statusIdLabel"),
        },
        {
          field: "protocolId",
          header: t("Permissions.protocolIdLabel"),
        },
        {
          field: "port",
          header: t("Permissions.portLabel"),
        },
        {
          field: "methodId",
          header: t("Permissions.methodIdLabel"),
        },
        {
          field: "pagePath",
          header: t("Permissions.pagePathLabel"),
        },
        {
          field: "servicePath",
          header: t("Permissions.servicePathLabel"),
        },
        {
          field: "expeditionDate",
          header: t("Functionalities.expeditionDateLabel"),
        },
        {
          field: "expirationDate",
          header: t("Functionalities.expirationDateLabel"),
        },
        {
          field: "createdAt",
          header: t("Permissions.createdAtLabel"),
        },
        {
          field: "deletedAt",
          header: t("Permissions.deletedAtLabel"),
        },
        {
          field: "actions",
          header: t("Permissions.actionsLabel"),
        },
      ];
      return isDeleteComponent
        ? columList.filter((obj) => obj.field !== 'statusId' && obj.field !== 'createdAt')
        : columList.filter((obj) => obj.field !== 'deletedAt')
    } else {
      return [];
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, language]);

  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    functionalityId: { value: pageFilter?.functionalityId, matchMode: FilterMatchMode.IN },
    privilegeId: { value: pageFilter?.privilegeId, matchMode: FilterMatchMode.IN },
    statusId: { value: pageFilter?.statusId, matchMode: FilterMatchMode.EQUALS },
    protocolId: { value: pageFilter?.protocolId, matchMode: FilterMatchMode.IN },
    methodId: { value: pageFilter?.methodId, matchMode: FilterMatchMode.IN },
    port: { value: pageFilter?.port, matchMode: FilterMatchMode.CONTAINS },
    pagePath: { value: pageFilter?.pagePath, matchMode: FilterMatchMode.CONTAINS },
    servicePath: { value: pageFilter?.servicePath, matchMode: FilterMatchMode.CONTAINS },
    expeditionDate: { value: pageFilter?.expeditionDate, matchMode: FilterMatchMode.CONTAINS },
    expirationDate: { value: pageFilter?.expirationDate, matchMode: FilterMatchMode.CONTAINS },
    createdAt: { value: pageFilter?.createdAt, matchMode: FilterMatchMode.CONTAINS },
  }
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [administrationFilters, setAdministrationFilters] = useState(defaultFilterValues);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const onAction = (type: "CLONE" | "UPDATE" | "DETAIL", data?: any) => {
    if (type === "UPDATE" && data) {
      setAppState((prev) => ({
        ...prev,
        selectedPermission: data,
      }));
      navigate("/permissions/editpermissions");
    }
    if (type === "CLONE" && data) {
      setAppState((prev) => ({
        ...prev,
        selectedPermission: data,
      }));
      navigate("/permissions/clonepermissions");
    }
    if (type === "DETAIL" && data) {
      setAppState((prev) => ({
        ...prev,
        selectedPermission: data,
      }));
      navigate("/permissions/detailpermissions");
    }
  };

  const onDeleteAction = async (data: any) => {
    const actionResult = await permissionsAction("DELETE", {id: data?.id, name: data?.name});
    if (actionResult === "SUCCESS") {
      reloadFn();
      setAdministrationFilters(defaultFilterValues);
    }
  };

  const onReactiveAction = async (data: any) => {
    const actionResult = await permissionsAction("REACTIVE", {id: data?.id, name: data?.name});
    if (actionResult === "SUCCESS") {
      reloadFn();
      setAdministrationFilters(defaultFilterValues);
    }
  };

  const handleModalProps = async (type: 'DELETE' | 'REACTIVE', data: any) => {
    setModalProps((prev:any) => ({
      ...prev,
      visible: true,
      title: type === 'DELETE' ? t("Permissions.deleteModalTitle") : t("Permissions.deleteModalTitle"),
      content: `${t("Permissions.modalContent1")} ${type === 'DELETE' ? t("Permissions.deleteModalContent2") : t("Permissions.reactiveModalContent2")}`,
      onConfirm: () => {
        type === 'DELETE' ? onDeleteAction(data!) : onReactiveAction(data!);
      },
    }));
  };

  const onClearFiltersColumn = () => {
    setAdministrationFilters(defaultFilterValues);
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(formBlankValues);
  };

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(`Permissions.${col.field}Label`),
        }))
      );
  }, [t, language, setSelectedColumns]);

  const filterTemplate = (options: any) => {
    return (
      <Controller
          name={options.field}
          control={control}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.target.value);
              options.filterCallback(e.target.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-2">
                  {t(`Permissions.${options.field}Label`)}
                </label>
                <InputText
                  placeholder={t(`Permissions.${options.field}PlaceholderLabel`)} 
                  autoComplete="off"
                  type={'text'}
                  className="mb-2"
                  {...field}
                />
              </>
            );
          }}
      />
    )
  };

  const filterClearTemplate = (options: any) => {
      return (
        <Button
          label={t("Permissions.clearFilterLabel")}
          variant="p-button-text"
          onClick={onClearFiltersColumn}
        />
      )
  };
    
  const filterApplyTemplate = (options: any) => {
      return (
        <Button
          label={t("Permissions.applyFilterLabel")}
          className="p-button"
          onClick={()=>{
            options.filterApplyCallback();
            setAppState((prev: any)=>({
              ...prev,
              pageFilter: getValues(),
            }));
          }}
        />
      )
  };

  const statusBodyTemplate = (rowData:any) => (
    <Tag
      value={rowData.statusId}
      severity={
        (['active', 'activo'].includes(rowData.statusId.toLowerCase()) && 'success') ||
        (['draft', 'borrador'].includes(rowData.statusId.toLowerCase()) && 'info') ||
        (['inactive', 'inactivo'].includes(rowData.statusId.toLowerCase()) && 'danger') ||
        'warning'
      }
    />
  );

  const ItemsTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option}</span>
      </div>
    );
  };
  const multiselectFilterTemplate = (options: any) => {
    return (
      <Controller
          name={options.field}
          control={control}
          // rules={PermissionsFormSchema(t).name}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
              options.filterCallback(e.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-1">
                  {t(`Permissions.${options.field}Label`)}
                </label>
                <MultiSelect
                  display="chip"
                  options={[...new Set(data.filter((permission: any) => permission.statusId !== 'DELETED').map((permission: any) => permission[`${options.field}`]))]} 
                  itemTemplate={ItemsTemplate}
                  placeholder={t(`Permissions.${options.field}PlaceholderLabel`)} 
                  className="mb-2"
                  emptyFilterMessage={t("Permissions.noAvailableMessage")}
                  filter
                  {...field}
                />
              </>
            );
          }}
      />
    )
  };

  const statuses = [
    `${t("Permissions.ACTIVELabel")}`,
    `${t("Permissions.INACTIVELabel")}`,
    `${t("Permissions.DRAFTLabel")}`,
  ];
  const statusItemTemplate = (option: any) => {
    return <span>{option}</span>;
  };
  const statusFilterTemplate = (options: any) => {
    return (
      <Controller
            name={options.field}
            control={control}
            render={({ field }) => {
              field.onChange = (e) => {
                setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
                options.filterCallback(e.value);
              };
              return (
                <>
                  <label htmlFor={options.field} className="block lb mt-2 mb-3">
                    {t(`Permissions.${options.field}Label`)}
                  </label>
                  {/* // @ts-ignore */}
                  <Dropdown
                    options={statuses}
                    itemTemplate={statusItemTemplate}
                    placeholder={t(`Permissions.${options.field}PlaceholderLabel`)}
                    className="p-column-filter mb-2"
                    showClear={false}
                    {...field} 
                  />
                </>
              );
            }}
          />
      
    )
  };

  const dateFilterTemplate = (options: any) => {
    return (
        <Calendar
          label={t(`Permissions.${options.field}Label`)}
          value={options.value}
          onChange={(e) => options.filterCallback(e.value, options.index)}
          dateFormat="mm/dd/yy"
          placeholder="mm/dd/yyyy"
          mask="99/99/9999"
          className="mt-2 mb-2"
        />
      )
  };

  useEffect(() => {
    setAdministrationFilters(defaultFilterValues);
  }, [pageFilter]);

  useEffect(() => {
    if (sessionStorage.getItem('filters-table')) {
      const dataSession = JSON.parse(sessionStorage.getItem('filters-table') || "");
      const global = dataSession.filters.global;
      setGlobalFilterValue(global);
    }
  }, [administrationFilters]);

  return (
    <>
      {!isDeleteComponent &&
        <BoxFull className="mt-0 mx-0 px-0 pb-3" variant="tertiary">
          <div className="subheader-buttons">
            <SubheaderActions
              className="p-0 m-0"
              actions={subheaderActionsPrivilegeValidator([
                {
                  id: "create-catalogue",
                  icon: "iu iu-icon-plus",
                  label: t("Permissions.newPermissionBtnLabel"),
                  style: {
                    display: !permissiondInfoByRole.CREATE ? 'none' : undefined,
                  },
                  onClick: () => navigate("/permissions/newpermissions"),
                },
              ])}
            />
          </div>
        </BoxFull>
      }
      <BoxFull className={`dt-c mx-0 ${isDeleteComponent && "mt-5"} mb-4`} variant="tertiary" style={{ backgroundColor: 'var(--cardBackgroundColorOne)' }}>
        {data ? (
          <DataTable
            header={
              <TableHeader
                title={t(`Permissions.permissionsTableTitle`)}
                columns={columns}
                selectedColumns={selectedColumns}
                toggleColumns={setSelectedColumns}
                filters={administrationFilters}
                setFilters={setAdministrationFilters}
                reload={reloadFn}
                onClearFiltersColumn={onClearFiltersColumn}
                globalFilterValue={globalFilterValue}
              />
            }
            currentPageReportTemplate={t(
              "ConsoleTranslations.rowsPaginatorTemplateLabel"
            )}
            paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
            emptyMessage={<EmptyTemplate image={noResultsImage} text={t('Permissions.emptyDataTableMessage')} />}
            rows={10}
            size="normal"
            filters={administrationFilters}
            sortField="updatedAt"
            dataKey="catalogue-id"
            sortOrder={-1}
            value={
              data?.filter((permissionItem: any) => {
                return isDeleteComponent
                  ? permissionItem?.statusId === 'DELETED'
                  : permissionItem?.statusId !== 'DELETED'
              })
              .map((permissionItem: any) => ({
              ...permissionItem,
              statusId: t(`Permissions.${permissionItem?.statusId}Label`),
              expeditionDate: !!permissionItem?.expeditionDate ? formatStartDateAndEndDate(permissionItem?.expeditionDate, true, t) : null,
              expirationDate: !!permissionItem?.expirationDate ? formatStartDateAndEndDate(permissionItem?.expirationDate, true, t) : null,
              createdAt: !!permissionItem?.createdAt ? formatStringToDate2(permissionItem?.createdAt, true, t) : null,
              deletedAt: !!permissionItem?.deletedAt ? formatStringToDate2(permissionItem?.deletedAt, true, t) : null,
              actions: !isDeleteComponent
              ? (
                  <Actions
                    enableDelete
                    auxButtons={[
                      {
                        label: t("Roles.detailBtnLabel"),
                        icon: "iu-icon-detail",
                        function: () => {
                          onAction("DETAIL", permissionItem);
                          window.scrollTo(0,0);
                        },
                        color: "var(--successColor)",
                        order: "-3",
                      },
                      {
                        label: t("Permissions.cloneBtnLabel"),
                        icon: "iu-icon-copy",
                        function: () => onAction("CLONE", permissionItem),
                        color: "var(--successColor)",
                        order: "-1",
                        showButton: permissiondInfoByRole.CREATE
                      }
                    ]}
                    onEdit={() => onAction("UPDATE", permissionItem)}
                    onDelete={() => handleModalProps('DELETE', permissionItem)}
                    visibleObject={{...permissiondInfoByRole}}
                  />
                )
              : (
                <Button
                  className="p-button-rounded p-button-success p-button-outlined act-btn"
                  aria-label="reactive"
                  tooltip={t("Permissions.reactiveLabel")}
                  tooltipOptions={{ position: "bottom" }}
                  variant="p-button"
                  onClick={() => handleModalProps('REACTIVE', permissionItem)}
                  style={{
                    color: "var(--successColor)",
                  }}
                >
                  <img
                    alt='reactive-icon'
                    src={reactiveIcon}
                    width='15px'
                  />
                </Button>
              )
                ,
              }))}
            scrollable
            resizableColumns
            globalFilterFields={[
              "functionalityId",
              "privilegeId",
              "statusId",
              "protocolId",
              "methodId",
              "port",
              "pagePath",
              "servicePath",
              "expeditionDate",
              "expirationDate",
              "createdAt",
              "deletedAt",
            ]}
            filterDisplay="menu"
            rowsPerPageOptions={[10,20,50]}
            stateStorage="session"
            stateKey="filters-table"
            >
            {selectedColumns.map((col: any) => {
              return (
                <Column
                  key={col.field}
                  field={col.field}
                  header={col.header}
                  sortable={!(col.field === "actions")}
                  align={col.field === "actions"  ? "center" : "left"}
                  headerStyle={
                    col.field === "actions" 
                    ? { justifyContent: "center" }
                    : undefined
                  }
                  style={{
                    boxShadow:
                    col.field === "actions"
                    ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                    : undefined,
                    maxWidth: col.field === "actions" ? "8.25rem" : "100%",
                    whiteSpace: "normal",
                    minWidth: (col.field === "actions") ? "12rem" : "14rem",
                  }}
                  frozen={col.field === "actions" ? true : false}
                  alignFrozen={col.field === "actions" ? "right" : undefined}
                  filterField={col.field}
                  showFilterMatchModes={false}
                  filterMenuStyle={{ width: "23rem" }}
                  filter={col.field === "actions" ? false : true}
                  filterElement={
                    ((col.field === "functionalityId" || col.field === "privilegeId" || col.field === "protocolId" || col.field === "methodId")  && multiselectFilterTemplate) ||
                    ((col.field === "port" || col.field === "pagePath" || col.field === "servicePath")  && filterTemplate) ||
                    (col.field === "statusId" && statusFilterTemplate) ||
                    ((col.field === "expeditionDate" || col.field === "expirationDate" || col.field === "createdAt") && dateFilterTemplate)
                  }
                  filterApply={filterApplyTemplate}
                  filterClear={filterClearTemplate}
                  dataType={(col.field === "expeditionDate" || col.field === "expirationDate" || col.field === "createdAt") ? "date" : ""}
                  // body={(col.field === "expeditionDate" || col.field === "expirationDate" || col.field === "createdAt") && dateBodyTemplate}
                  body={col.field === "statusId" ? statusBodyTemplate : undefined}
                />
                );
              })}
          </DataTable>
        ) : (
          <FormSkeleton length={8} />
          )}
      </BoxFull>
      <CustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
